import React, { useState, useEffect } from 'react';
import { Plus, Package, Trash2, Edit3 } from 'lucide-react';
import { API_BASE_URL, ENDPOINTS } from '../config/constants';

interface Gift {
  id: number;
  name: string;
  price: number;
  isActive: boolean;
  imageKey?: string;
  imageUrl?: string;
  createdAt?: string;
}

const GiftList: React.FC = () => {
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGifts();
  }, []);

  const fetchGifts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.GIFTS}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      });

      if (!response.ok) {
        throw new Error('Hediyeler yüklenirken hata oluştu');
      }

      const data = await response.json();
      setGifts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu hediyeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.GIFTS}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      });

      if (response.ok) {
        setGifts(gifts.filter(gift => gift.id !== parseInt(id)));
      } else {
        throw new Error('Hediye silinirken hata oluştu');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Package className="mr-3" size={28} />
            Hediyeler
          </h1>
          <p className="text-gray-600 mt-1">AI Girlfriend'ler için hediyeler yönetin</p>
        </div>
        <a
          href="/gifts/create"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
        >
          <Plus size={18} className="mr-2" />
          Yeni Hediye Ekle
        </a>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Gift Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {gifts.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Package size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz hediye yok</h3>
            <p className="text-gray-600 mb-4">İlk hediyenizi oluşturmak için başlayın</p>
            <a
              href="/gifts/create"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center transition-colors"
            >
              <Plus size={18} className="mr-2" />
              Yeni Hediye Ekle
            </a>
          </div>
        ) : (
          gifts.map((gift) => (
            <div key={gift.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
              {/* Gift Image */}
              <div className="h-48 bg-gray-100 relative">
                {gift.imageUrl ? (
                  <img
                    src={gift.imageUrl}
                    alt={gift.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Package size={48} className="text-gray-400" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    gift.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {gift.isActive ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
              </div>

              {/* Gift Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2 truncate">{gift.name}</h3>
                <div className="text-lg font-bold text-blue-600 mb-3">
                  {gift.price}
                </div>

                {/* Actions */}
                <div className="flex justify-end items-center space-x-1">
                  <a
                    href={`/gifts/edit/${gift.id}`}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Düzenle"
                  >
                    <Edit3 size={16} />
                  </a>
                  <button
                    onClick={() => handleDelete(gift.id.toString())}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Sil"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GiftList; 